import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { getFormValues, reduxForm } from 'redux-form';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { RootState } from 'af-reducers';

import { COMPANY_POLICY } from 'af-constants/reduxForms';

import { Button, errorToast, loadingToast, successToast } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';

import * as CompanyActions from 'af-actions/companies';

import CompanyPolicyForm from './CompanyPolicyForm';
import { CompanyPolicyFM } from './formModel';

type FormProps = InjectedFormProps<CompanyPolicyFM, FormOwnProps>;
type FormOwnProps = OwnProps & ConnectedProps<typeof connector>;

type OwnProps = {
	showModal: boolean;
	closeModal: () => void;
	initialValues: FormProps['initialValues'];
};

type Props = OwnProps & ConnectedProps<typeof connector> & InjectedFormProps<CompanyPolicyFM>;

const CompanyPolicyEdit: React.FC<Props> = (props) => {

	const { closeModal, showModal, handleSubmit, reset, invalid, editCompanyPolicy, getCompany, fileNameForUpload } = props;

	const handleClose = React.useCallback(() => {
		closeModal();
		reset();
	}, [closeModal, reset]);

	const onSubmit = React.useCallback(async (form: CompanyPolicyFM) => {
		const acceptedToastId = form.deleteFile ? 'deleteFile' : fileNameForUpload;
		loadingToast(
			acceptedToastId,
			{
				title: form.deleteFile ? 'Deleting attachment...' : 'Uploading attachment...',
				description: fileNameForUpload,
				timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG),
			}

		);

		try {
			await editCompanyPolicy(CompanyPolicyFM.toRM(form));

			successToast(
				acceptedToastId,
				{
					title: form.deleteFile ? 'Deletion complete.' : 'Upload complete.',
					description: fileNameForUpload,
					timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG),
				}
			);

			await getCompany();
		} catch ({ errors }) {
			if (errors?.files) {
				errorToast(
					acceptedToastId,
					{
						title: form.deleteFile ? 'Deletion failed. Please try again.' : 'Upload failed. Please try again.',
						timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG),
					}
				);
			}
		}

		closeModal();
	}, [closeModal, editCompanyPolicy, getCompany, fileNameForUpload]);

	return (
		<CustomModal
			closeModal={handleClose}
			modalStyle="info"
			showModal={showModal}
			size="lg"
		>
			<CustomModal.Header
				closeModal={handleClose}
				title="Edit Company Policy"
			/>
			<CustomModal.Body>
				<CompanyPolicyForm
					{...props}
				/>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={handleClose}
					style="secondary"
				/>
				<SubmitButton
					disabled={invalid}
					label="Save"
					onClick={handleSubmit(onSubmit)}
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapStateToProps(state: RootState) {
	const currentFormValues = getFormValues(COMPANY_POLICY)(state) as CompanyPolicyFM;

	return {
		fileNameForUpload: currentFormValues?.files[0]?.name ?? null,
	};
}

function mapDispatchToProps() {
	return {
		getCompany: CompanyActions.getCompany,
		editCompanyPolicy: CompanyActions.editCompanyPolicy,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	React.memo,
	connector,
	reduxForm({ form: COMPANY_POLICY, enableReinitialize: true, validate: CompanyPolicyFM.validate })
);

export default enhance(CompanyPolicyEdit);
